import {
  type CreateCreditCardItemAllocationPayload,
  type CreateCreditCardItemPayload,
  type UpdateCreditCardItemPayload,
} from '@webapp/modules/accounts-payable/business-logic/interfaces/ICreditCardItemRepository'
import {
  type CreditCardItemFormAllocationValue,
  type CreditCardItemFormSchema,
} from '@webapp/modules/accounts-payable/components/CreditCardItemForm/CreditCardItemForm.types'

const mapAllocations = (
  allocations: CreditCardItemFormAllocationValue[]
): CreateCreditCardItemAllocationPayload[] =>
  allocations.reduce<CreateCreditCardItemAllocationPayload[]>((rows, row) => {
    // Prefilled rows carry the persisted budgetId; new rows resolve the
    // budget from the selected requestable account (the account row IS the
    // budget row). Unresolved rows are dropped.
    const budgetId = row.budgetId ?? row.account?.id

    return budgetId != null
      ? [...rows, { budget: budgetId, amount: row.amount }]
      : rows
  }, [])

export interface MapCreateCreditCardItemFormSchemaParams {
  schema: CreditCardItemFormSchema
  creditcardId: number
  /** Existing + newly uploaded attachment UUIDs (BHV-017) */
  packingSlipUuids: string[]
}

// Create payload rule (BHV-017): `unit_cost = total_cost`, `quantity = 1`.
export const mapCreateCreditCardItemFormSchemaToPayload = ({
  schema,
  creditcardId,
  packingSlipUuids,
}: MapCreateCreditCardItemFormSchemaParams): CreateCreditCardItemPayload => ({
  creditcard: creditcardId,
  description: schema.description,
  received_on: schema.date?.format('YYYY-MM-DD') ?? '',
  total_cost: schema.amount,
  unit_cost: schema.amount,
  quantity: 1,
  currency: schema.currency?.id ?? 0,
  cost_allocations: mapAllocations(schema.allocations),
  packing_slips: packingSlipUuids,
})

export interface MapUpdateCreditCardItemFormSchemaParams {
  schema: CreditCardItemFormSchema
  itemId: number
  mode: 'edit' | 'view'
  packingSlipUuids: string[]
}

// Edit sends the full normalized form; view mode sends only
// `{ id, cost_allocations }` (BHV-019).
export const mapUpdateCreditCardItemFormSchemaToPayload = ({
  schema,
  itemId,
  mode,
  packingSlipUuids,
}: MapUpdateCreditCardItemFormSchemaParams): UpdateCreditCardItemPayload => {
  if (mode === 'view') {
    return {
      id: itemId,
      cost_allocations: mapAllocations(schema.allocations),
    }
  }

  return {
    id: itemId,
    description: schema.description,
    total_cost: schema.amount,
    currency: schema.currency?.id,
    cost_allocations: mapAllocations(schema.allocations),
    packing_slips: packingSlipUuids,
  }
}
